import { useQuery } from "@tanstack/react-query";
import { get } from "../api/util";
import { createContext } from "react";
import Spinner from "../components/spinner/Spinner";
import AccessDenied from "../components/AccessDenied";
import DefaultLoadingError from "../components/error/DefaultLoadingError";
import ASLogo from "../components/icons/ASLogo";
import { AlertMessage } from "../components/AlertMessage";

const hasPermission = (
  data: UserDto,
  permissionType: UserPermissions,
): boolean => (data?.permissions ?? []).includes(permissionType);

export const UserQueryContext = createContext<{
  refetch: InvalidateDataFunction<any>;
  user: UserDto;
  hasPermission: typeof hasPermission;
}>({
  refetch: () => new Promise(() => {}),
  user: {
    id: "",
    initials: "",
    name: "",
    permissions: [],
    email: undefined,
  },
  hasPermission,
});

export const UserQueryProvider = ({ children }) => {
  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ["claims"],
    queryFn: async function () {
      return get<UserDto>({
        query: "claims",
      });
    },
  });

  if (isLoading) {
    return <Spinner label="Henter bruger ..." />;
  }

  if (error) {
    console.error(error);
    return <AccessDenied />;
  }

  if (!data) {
    return (
      <div className="h-100 p-4">
        <ASLogo height={60} />
        <div className="d-flex flex-column gap-4 h-100 align-items-center justify-content-center">
          <AlertMessage type="primary">
            <DefaultLoadingError
              label={
                <div className="d-flex flex-column gap-2 text-secondary">
                  <h5 className="alert-primary">
                    Brugerinformation kunne ikke findes.
                  </h5>
                  Klik 'Genindlæs' eller kontakt deres administrator, hvis
                  problemet fortsætter.
                </div>
              }
              refreshLabel="Genindlæs"
              onRefresh={() => refetch()}
            />
          </AlertMessage>
        </div>
      </div>
    );
  }

  if (!hasPermission(data, "BasisRead")) {
    return <AccessDenied />;
  }

  return (
    <UserQueryContext.Provider
      value={{
        refetch,
        hasPermission,
        user: data,
      }}
    >
      {children}
    </UserQueryContext.Provider>
  );
};

type UserPermissions =
  | "BasisRead"
  | "BasisWrite"
  | "AdminRead"
  | "AdminWrite"
  | "KursusRead"
  | "KursusWrite"
  | "TilsynRead"
  | "TilsynWrite"
  | "DisciplinaertRead"
  | "DisciplinaertWrite"
  // ADV-349: må se og rette CPR-løbenummeret (Clerk-rollen CprAdgang).
  | "CprRead";

interface User {
  id: string;
  name: string;
  initials: string;
  email?: string;
}

interface UserDto extends User {
  permissions: Array<UserPermissions>;
}
